import type { ComposerDraft, DraftDocument, SenderNameMode } from "../shared/types";

export type ResponsePerson = {
  responseId: string;
  id: string;
  name: string;
  avatarUrl?: string;
  mention: string;
  answer?: string;
  answers?: Array<{ label: string; value: string; type?: string; timezone?: string }>;
  timezone?: string;
  groupName?: string;
  submitted?: boolean;
  respondedAt: string;
};

export type SlackUserGroup = {
  id: string;
  handle: string;
  name: string;
  description: string;
  memberCount: number;
};

export type ResponseActivity = {
  id: string;
  name: string;
  avatarUrl?: string;
  mention: string;
  count: number;
  lastAt: string;
  actions: Record<string, number>;
};

export type HomeData = {
  user: { id: string; name: string; avatarUrl: string };
  drafts: Array<{
    id: string;
    title: string;
    ping: "channel" | "here" | "none";
    status: "draft" | "sending" | "sent" | "failed";
    channelId: string;
    channelName: string;
    updatedAt: string;
    sentAt?: string | null;
    publishedMessageId?: string | null;
  }>;
};

export type AdminPerson = {
  slackUserId: string;
  internalId: string | null;
  username: string;
  displayName: string;
  realName: string;
  avatarUrl: string;
  isAdmin: boolean;
  isOwner: boolean;
  deleted: boolean;
  banned: boolean;
  bannedAt: string | null;
  messageCount: number;
  lastMessageAt: string | null;
  raw: unknown;
};

export type AdminMessage = {
  id: string;
  draftId: string;
  publishedMessageId: string | null;
  slackMessageTs: string | null;
  title: string;
  status: string;
  ping: "channel" | "here" | "none";
  fallbackText: string;
  document: DraftDocument;
  senderName: string;
  sender: {
    slackUserId: string;
    username: string;
    displayName: string;
    realName: string;
    avatarUrl: string;
  };
  channel: { id: string; name: string; isPrivate: boolean };
  createdAt: string;
  updatedAt: string;
  sentAt: string | null;
  raw: unknown;
};

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    credentials: "same-origin",
    headers: { "content-type": "application/json", ...init?.headers },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new ApiError(body.message ?? body.error ?? "Request failed", response.status, body);
  return body as T;
}

type ClientCacheEntry = {
  value?: unknown;
  expiresAt: number;
  retryAt: number;
  pending?: Promise<unknown>;
};

const getCache = new Map<string, ClientCacheEntry>();
const MAX_CLIENT_CACHE_ENTRIES = 250;

function refreshCachedRequest<T>(url: string, ttlMs: number, entry: ClientCacheEntry) {
  if (entry.pending) return entry.pending as Promise<T>;
  const pending = request<T>(url)
    .then((value) => {
      entry.value = value;
      entry.expiresAt = Date.now() + ttlMs;
      entry.retryAt = 0;
      return value;
    })
    .catch((error) => {
      entry.retryAt = Date.now() + 30_000;
      if (entry.value !== undefined) return entry.value as T;
      throw error;
    })
    .finally(() => {
      entry.pending = undefined;
    });
  entry.pending = pending;
  return pending;
}

function cachedRequest<T>(url: string, ttlMs: number): Promise<T> {
  const now = Date.now();
  let entry = getCache.get(url);
  if (!entry) {
    if (getCache.size >= MAX_CLIENT_CACHE_ENTRIES) getCache.delete(getCache.keys().next().value ?? "");
    entry = { expiresAt: 0, retryAt: 0 };
    getCache.set(url, entry);
  }
  if (entry.value !== undefined) {
    if (entry.expiresAt <= now && entry.retryAt <= now && !entry.pending)
      void refreshCachedRequest<T>(url, ttlMs, entry);
    return Promise.resolve(entry.value as T);
  }
  if (entry.pending) return entry.pending as Promise<T>;
  return refreshCachedRequest<T>(url, ttlMs, entry);
}

function normalizedQuery(query: string) {
  return query.trim().toLowerCase().slice(0, 80);
}

export const api = {
  home: () => request<HomeData>("/api/home"),
  createHomeDraft: (input: {
    channelId: string;
    channelName: string;
    isPrivate: boolean;
    ping: "channel" | "here" | "none";
  }) =>
    request<{ draftId: string }>("/api/home/drafts", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  deleteHomeDraft: (draftId: string) => request<{ ok: true }>(`/api/home/drafts/${draftId}`, { method: "DELETE" }),
  logout: () => request<{ ok: true }>("/auth/logout", { method: "POST" }),
  adminPeople: (query: string) =>
    request<AdminPerson[]>(`/api/admin/people?q=${encodeURIComponent(query.trim().slice(0, 160))}`),
  adminMessages: (query: string, sender = "") => {
    const search = new URLSearchParams();
    if (query.trim()) search.set("q", query.trim().slice(0, 300));
    if (sender) search.set("sender", sender);
    return request<AdminMessage[]>(`/api/admin/messages?${search}`);
  },
  setAdminBan: (slackUserId: string, banned: boolean) =>
    request<{ banned: boolean }>(`/api/admin/users/${encodeURIComponent(slackUserId)}/ban`, {
      method: banned ? "POST" : "DELETE",
    }),
  draft: (id: string) => request<ComposerDraft>(`/api/drafts/${id}`),
  draftHead: (id: string) => request<{ version: number; updatedAt: string }>(`/api/drafts/${id}/head`),
  saveDraft: (
    id: string,
    document: DraftDocument,
    expectedVersion: number,
    title: string,
    identityId?: string | null,
  ) =>
    request<{ version: number; updatedAt: string }>(`/api/drafts/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ document, expectedVersion, title, identityId }),
    }),
  sendDraft: (id: string) =>
    request<{ messageId: string; slackTs: string; channelId: string; senderName: string }>(`/api/drafts/${id}/send`, {
      method: "POST",
    }),
  pingPermission: (id: string) =>
    request<{
      allowed: boolean;
      reason: string;
      creator?: { slackUserId: string; name: string; avatarUrl?: string };
      managers: Array<{ slackUserId: string; name: string; avatarUrl?: string }>;
      canRequest: boolean;
      hasDenial: boolean;
      activeRequest?: { id: string; status: string; ping: string };
    }>(`/api/drafts/${id}/ping-permission`),
  requestPing: (id: string, approverSlackUserId?: string) =>
    request<{ requestId: string; notified: number; message: string }>(
      `/api/drafts/${id}/ping-request${approverSlackUserId ? `?ask=${encodeURIComponent(approverSlackUserId)}` : ""}`,
      { method: "POST" },
    ),
  cancelPing: (id: string) =>
    request<{ cancelled: boolean; message: string }>(`/api/drafts/${id}/ping-request`, { method: "DELETE" }),
  users: (query: string) =>
    cachedRequest<Array<{ id: string; name: string; handle: string; realName?: string; avatarUrl: string }>>(
      `/api/search/users?q=${encodeURIComponent(normalizedQuery(query))}`,
      30 * 60_000,
    ),
  channels: (query: string) =>
    cachedRequest<Array<{ id: string; name: string; isPrivate: boolean }>>(
      `/api/search/channels?q=${encodeURIComponent(normalizedQuery(query))}`,
      30 * 60_000,
    ),
  usergroups: () => cachedRequest<SlackUserGroup[]>("/api/usergroups", 60_000),
  usergroup: (id: string) => request<SlackUserGroup | null>(`/api/usergroups/${encodeURIComponent(id)}`),

  emoji: () => cachedRequest<Record<string, string>>("/api/emoji", 6 * 60 * 60_000),
  emojiList: () =>
    cachedRequest<
      Array<{
        name: string;
        imageUrl: string;
        alias: string | null;
      }>
    >("/api/emojis", 6 * 60 * 60_000),
  emojiInfo: (name: string) =>
    request<{ name: string; imageUrl: string; alias: string | null }>(`/api/emojis/${encodeURIComponent(name)}`),
  timezone: () =>
    request<{ timezone: string; label?: string; offsetSeconds?: number; source: "slack" | "slack_web" }>(
      "/api/profile/timezone",
    ),
  senderName: (mode: SenderNameMode) =>
    request<{ mode: SenderNameMode; selectedName: string }>("/api/profile/sender-name", {
      method: "PATCH",
      body: JSON.stringify({ mode }),
    }),
  identities: () => cachedRequest<Array<{ id: string; name: string; avatarUrl: string }>>("/api/identities", 60_000),
  createIdentity: (name: string) =>
    request<{ id: string; name: string; avatarUrl: string }>("/api/identities", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),
  renameIdentity: (id: string, name: string) =>
    request<{ id: string; name: string }>(`/api/identities/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),
  archiveIdentity: (id: string) =>
    request<{ id: string; archived: boolean }>(`/api/identities/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),
  uploadIdentityAvatar: (id: string, blob: Blob, mimeType: string) =>
    request<{ id: string; name: string; avatarUrl: string }>(`/api/identities/${encodeURIComponent(id)}/avatar`, {
      method: "PATCH",
      headers: { "content-type": mimeType },
      body: blob,
    }),
  resolveMentions: (users: string[], channels: string[]) =>
    request<{
      users: Array<{ id: string; name: string }>;
      channels: Array<{ id: string; name: string }>;
    }>("/api/resolve-mentions", { method: "POST", body: JSON.stringify({ users, channels }) }),
  results: (id: string) =>
    request<{
      messageId: string;
      votes: Array<{
        id: string;
        question: string;
        closed: boolean;
        showVoters: boolean;
        showCounts: boolean;
        showPercentages: boolean;
        showBars: boolean;
        options: Array<{
          id: string;
          label: string;
          description?: string;
          componentId?: string;
          people: ResponsePerson[];
          activity: ResponseActivity[];
        }>;
      }>;
      rsvps: Array<{
        id: string;
        componentId?: string;
        label: string;
        eventTitle: string;
        eventStartsAt?: string;
        eventTimezone?: string;
        people: ResponsePerson[];
        activity: ResponseActivity[];
      }>;
      components: Array<{
        id: string;
        label: string;
        behavior: string;
        config: Record<string, unknown>;
        people: ResponsePerson[];
        activity: ResponseActivity[];
      }>;
    }>(`/api/messages/${id}/results`),
  setVoteClosed: (messageId: string, voteId: string, closed: boolean) =>
    request<{ closed: boolean }>(`/api/messages/${encodeURIComponent(messageId)}/votes/${encodeURIComponent(voteId)}`, {
      method: "PATCH",
      body: JSON.stringify({ closed }),
    }),
  bootstrap: () => request<{ draftId: string }>("/dev/bootstrap", { method: "POST" }),
  async upload(draftId: string, file: File, onProgress: (percent: number) => void) {
    const reservation = await request<{ assetId: string; uploadUrl: string; publicUrl: string }>(
      "/api/uploads/presign",
      {
        method: "POST",
        body: JSON.stringify({ draftId, name: file.name, mimeType: file.type, size: file.size }),
      },
    );
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", reservation.uploadUrl);
      xhr.timeout = 130_000;
      xhr.setRequestHeader("Content-Type", file.type);
      xhr.upload.onprogress = (event) =>
        event.lengthComputable && onProgress(Math.min(99, Math.round((event.loaded / event.total) * 100)));
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress(100);
          resolve();
        } else {
          const message = (() => {
            try {
              return JSON.parse(xhr.responseText).message as string | undefined;
            } catch {
              return undefined;
            }
          })();
          reject(new Error(message || `Upload failed (${xhr.status})`));
        }
      };
      xhr.onerror = () => reject(new Error("Upload connection failed"));
      xhr.ontimeout = () => reject(new Error("Upload timed out. Try again."));
      xhr.onabort = () => reject(new Error("Upload canceled"));
      xhr.send(file);
    });
    return reservation;
  },
};
